'use client'

import {
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  AnimatePresence,
  motion,
} from 'framer-motion'

import {
  X,
  Paperclip,
  Smile,
  LockKeyhole,
  Loader2,
  Check,
  FileText,
  Lightbulb,
  HelpCircle,
  Trophy,
  BookOpen,
  ChevronDown,
  Send,
} from 'lucide-react'

import { cn } from '@/lib/utils'

import type {
  EncounterContributionType,
} from '@/components/community/feed/types'

import type {
  useContextPublishComposer,
} from './useContextPublishComposer'

/* -------------------------------------------------------------------------- */
/*                                   TYPES                                    */
/* -------------------------------------------------------------------------- */

type ContextPublishComposerExtrasProps = {
  composer:
    ReturnType<
      typeof useContextPublishComposer
    >
}

/* -------------------------------------------------------------------------- */
/*                                  CONSTANTS                                 */
/* -------------------------------------------------------------------------- */

const CONTRIBUTION_TYPES: Array<{
  value:
    EncounterContributionType
  label:
    string
  icon:
    typeof Trophy
}> = [
  {
    value: 'result',
    label: 'Resultado',
    icon: Trophy,
  },
  {
    value: 'idea',
    label: 'Idea',
    icon: Lightbulb,
  },
  {
    value: 'resource',
    label: 'Recurso',
    icon: BookOpen,
  },
  {
    value: 'question',
    label: 'Pregunta',
    icon: HelpCircle,
  },
]

const QUICK_EMOJIS = [
  '🔥',
  '👏',
  '🚀',
  '💡',
  '❤️',
  '✅',
  '🎯',
  '🙌',
  '🤔',
  '💪',
  '🏆',
  '✨',
]

/* -------------------------------------------------------------------------- */
/*                                COMPONENT                                   */
/* -------------------------------------------------------------------------- */

export function ContextPublishComposerExtras({
  composer,
}: ContextPublishComposerExtrasProps) {
  const [
    emojiOpen,
    setEmojiOpen,
  ] =
    useState(false)

  const [
    premiumOpen,
    setPremiumOpen,
  ] =
    useState(false)

  const fileInputRef =
    useRef<HTMLInputElement | null>(
      null
    )

  useEffect(() => {
    if (
      composer.needsContributionType &&
      !composer.contributionType &&
      composer.suggestedContributionType
    ) {
      composer.setContributionType(
        composer.suggestedContributionType
      )
    }
  }, [
    composer.needsContributionType,
    composer.contributionType,
    composer.suggestedContributionType,
    composer.setContributionType,
  ])

  /* ---------------------------------------------------------------------- */
  /*                               SUBMIT                                   */
  /* ---------------------------------------------------------------------- */

  async function handlePublish() {
    if (
      !composer.canSubmit ||
      composer.publishing
    ) {
      return
    }

    /*
     * El hook es ahora la única
     * fuente de verdad del publish.
     *
     * useContextPublishComposer
     * ejecutará su propio onPublished
     * cuando termine correctamente.
     */
    await composer.publish()
  }

  return (
    <div className="w-full">
      {/* ------------------------------------------------------------------ */}
      {/* CONTRIBUTION TYPE                                                   */}
      {/* ------------------------------------------------------------------ */}

      {composer.needsContributionType ? (
        <div className="mt-3 border-t border-border/40 px-1 pt-3">
          <div className="mb-2 text-[11px] font-medium text-muted-foreground">
            Tipo de aporte
          </div>

          <div className="flex flex-wrap gap-2">
            {CONTRIBUTION_TYPES.map(
              item => {
                const Icon =
                  item.icon

                const active =
                  composer.contributionType ===
                  item.value

                return (
                  <button
                    key={
                      item.value
                    }
                    type="button"
                    onClick={() =>
                      composer.setContributionType(
                        item.value
                      )
                    }
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition',

                      active
                        ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                        : 'border-border/70 bg-background/60 text-muted-foreground hover:border-foreground/20 hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />

                    {item.label}
                  </button>
                )
              }
            )}
          </div>
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* FILE PREVIEWS                                                       */}
      {/* ------------------------------------------------------------------ */}

      {composer.files.length >
      0 ? (
        <div className="mt-4 grid grid-cols-2 gap-2 px-1 sm:grid-cols-4">
          {composer.files.map(
            item => (
              <div
                key={
                  item.id
                }
                className="group relative overflow-hidden rounded-2xl border border-border bg-muted/40"
              >
                {item.isImage &&
                item.previewUrl ? (
                  <img
                    src={
                      item.previewUrl
                    }
                    alt={
                      item.file.name
                    }
                    className="aspect-square h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex aspect-square flex-col items-center justify-center gap-2 p-3 text-center">
                    <FileText className="h-6 w-6 text-muted-foreground" />

                    <div className="line-clamp-2 max-w-full text-[11px] text-muted-foreground">
                      {
                        item.file
                          .name
                      }
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() =>
                    composer.removeFile(
                      item.id
                    )
                  }
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-background/85 text-foreground opacity-100 shadow-sm backdrop-blur transition hover:bg-background sm:opacity-0 sm:group-hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )
          )}
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* PREMIUM                                                             */}
      {/* ------------------------------------------------------------------ */}

      <AnimatePresence initial={false}>
        {premiumOpen &&
        composer.canUsePremiumContent ? (
          <motion.div
            initial={{
              opacity: 0,
              height: 0,
              y: -4,
            }}
            animate={{
              opacity: 1,
              height: 'auto',
              y: 0,
            }}
            exit={{
              opacity: 0,
              height: 0,
              y: -4,
            }}
            transition={{
              duration: 0.2,
            }}
            className="overflow-hidden"
          >
            <div className="mx-1 mt-4 rounded-2xl border border-border bg-muted/30 p-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-foreground">
                <LockKeyhole className="h-3.5 w-3.5" />

                Contenido premium
              </div>

              {composer.productsLoading ? (
                <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />

                  Cargando productos…
                </div>
              ) : composer.products.length >
                0 ? (
                <div className="relative">
                  <select
                    value={
                      composer.selectedProductId ||
                      ''
                    }
                    onChange={event => {
                      const value =
                        Number(
                          event.target
                            .value ||
                            0
                        )

                      composer.setSelectedProductId(
                        value ||
                          null
                      )
                    }}
                    className="w-full appearance-none rounded-xl border border-border bg-background px-3 py-2.5 pr-9 text-sm text-foreground outline-none transition focus:border-primary/50"
                  >
                    <option value="">
                      Sin cobro
                    </option>

                    {composer.products.map(
                      product => (
                        <option
                          key={
                            product.id
                          }
                          value={
                            product.id
                          }
                        >
                          {
                            product.name
                          }{' '}
                          ·{' '}
                          {
                            product.priceLabel
                          }
                        </option>
                      )
                    )}
                  </select>

                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              ) : (
                <div className="py-2 text-xs text-muted-foreground">
                  No tienes productos disponibles para vincular.
                </div>
              )}

              {composer.selectedProduct ? (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Este aporte estará vinculado a{' '}
                  <span className="font-medium text-foreground">
                    {
                      composer
                        .selectedProduct
                        .name
                    }
                  </span>
                  .
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* ------------------------------------------------------------------ */}
      {/* EMOJIS                                                              */}
      {/* ------------------------------------------------------------------ */}

      <AnimatePresence initial={false}>
        {emojiOpen ? (
          <motion.div
            initial={{
              opacity: 0,
              height: 0,
              y: -3,
            }}
            animate={{
              opacity: 1,
              height: 'auto',
              y: 0,
            }}
            exit={{
              opacity: 0,
              height: 0,
              y: -3,
            }}
            transition={{
              duration: 0.18,
            }}
            className="overflow-hidden"
          >
            <div className="mx-1 mt-4 flex flex-wrap gap-1.5 rounded-2xl border border-border bg-muted/30 p-2">
              {QUICK_EMOJIS.map(
                emoji => (
                  <button
                    key={
                      emoji
                    }
                    type="button"
                    onClick={() => {
                      composer.appendEmoji(
                        emoji
                      )
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-xl text-lg transition hover:bg-background hover:shadow-sm"
                  >
                    {emoji}
                  </button>
                )
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* ------------------------------------------------------------------ */}
      {/* ERROR                                                               */}
      {/* ------------------------------------------------------------------ */}

      {composer.error ? (
        <div className="mx-1 mt-4 rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {composer.error}
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* FOOTER                                                              */}
      {/* ------------------------------------------------------------------ */}

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border/60 px-1 pt-3">
        <div className="flex items-center gap-1">
          <input
            ref={
              fileInputRef
            }
            type="file"
            multiple
            className="hidden"
            onChange={event => {
              if (
                event.target
                  .files
              ) {
                composer.addFiles(
                  event.target
                    .files
                )
              }

              event.target.value =
                ''
            }}
          />

          <button
            type="button"
            disabled={
              composer.files
                .length >=
              composer.maxFiles
            }
            onClick={() =>
              fileInputRef.current?.click()
            }
            className="flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Paperclip className="h-4 w-4" />

            <span className="hidden sm:inline">
              Adjuntar
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setEmojiOpen(
                previous =>
                  !previous
              )
            }
            className={cn(
              'flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition',

              emojiOpen
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Smile className="h-4 w-4" />

            <span className="hidden sm:inline">
              Emoji
            </span>
          </button>

          {composer.canUsePremiumContent ? (
            <button
              type="button"
              onClick={() =>
                setPremiumOpen(
                  previous =>
                    !previous
                )
              }
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition',

                premiumOpen ||
                composer.selectedProductId
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <LockKeyhole className="h-4 w-4" />

              <span className="hidden sm:inline">
                Premium
              </span>
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          {!composer.canSubmit &&
          composer.validation.reason ? (
            <div className="hidden max-w-[220px] text-right text-[11px] text-muted-foreground sm:block">
              {
                composer
                  .validation
                  .reason
              }
            </div>
          ) : null}

          <button
            type="button"
            disabled={
              !composer.canSubmit ||
              composer.publishing
            }
            onClick={() =>
              void handlePublish()
            }
            className={cn(
              'inline-flex h-10 min-w-[104px] items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold transition',

              composer.status ===
                'success'
                ? 'bg-emerald-500 text-white'
                : 'bg-primary text-primary-foreground hover:bg-primary/90',

              (!composer.canSubmit ||
                composer.publishing) &&
                'cursor-not-allowed opacity-45'
            )}
          >
            {composer.publishing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />

                Publicando
              </>
            ) : composer.status ===
              'success' ? (
              <>
                <Check className="h-4 w-4" />

                Publicado
              </>
            ) : (
              <>
                Publicar

                <Send className="h-3.5 w-3.5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}